import { SupportedTokenName } from "@mezo/sdk"
import { createSlice } from "@reduxjs/toolkit"

import { LOCK_TIMES } from "#/shared/constants"

type DepositActionType =
  | "extend"
  | "resume"
  | "withdraw"
  | "redemption"
  | "migration"
  | null

export type DepositActionStatus =
  | "idle"
  | "in-progress"
  | "processing"
  | "completed"
  | "error"

export type WithdrawalSteps = "idle" | "retrieve" | "withdraw"

export type LockState = {
  lockEnd: string
  currentLockDuration: number
  newLockDuration: number
}

export type WithdrawalState = {
  withdrawalStep: WithdrawalSteps
}

export type DepositActionState = {
  id: string | null
  depositId: number | null
  userAmount: string
  amount: bigint
  token: SupportedTokenName | null
  dailyMats: string
  actionType: DepositActionType
  actionStatus: DepositActionStatus
}

type FullDepositActionState = DepositActionState & LockState & WithdrawalState

type InitializeFlow<T extends DepositActionState> = Omit<
  T,
  "actionStatus" | "withdrawalStep"
>

type InitializeMigrationFlow = Pick<
  DepositActionState,
  "id" | "depositId" | "token" | "actionStatus"
>

const initialState: FullDepositActionState = {
  id: null,
  depositId: null,
  userAmount: "",
  amount: 0n,
  token: null,
  dailyMats: "0",
  actionType: null,
  actionStatus: "idle",
  lockEnd: new Date().toISOString(),
  currentLockDuration: 0,
  newLockDuration: LOCK_TIMES[0].period,
  withdrawalStep: "idle",
}

const depositActionSlice = createSlice({
  name: "deposit-action",
  initialState,
  reducers: {
    initializeLockFlow: (
      immerState,
      {
        payload,
      }: {
        payload: Omit<
          InitializeFlow<
            FullDepositActionState & { actionType: "extend" | "resume" }
          >,
          "newLockDuration"
        >
      },
    ) => ({
      ...immerState,
      ...payload,
      newLockDuration: LOCK_TIMES[0].period, // we should always set highest lock time as default
    }),
    initializeWithdrawalFlow: (
      immerState,
      {
        payload,
      }: {
        payload: InitializeFlow<
          DepositActionState & { actionType: "withdraw" | "redemption" }
        >
      },
    ) => ({ ...immerState, ...payload }),
    initializeMigrationFlow: (
      immerState,
      {
        payload,
      }: {
        payload: InitializeMigrationFlow
      },
    ) => ({ ...immerState, ...payload, actionType: "migration" }),
    setNewLockDuration: (immerState, { payload }: { payload: number }) => {
      immerState.newLockDuration = payload
    },
    setActionStatus: (
      immerState,
      { payload }: { payload: DepositActionStatus },
    ) => {
      immerState.actionStatus = payload
    },
    setWithdrawalStep: (
      immerState,
      { payload }: { payload: WithdrawalSteps },
    ) => {
      immerState.withdrawalStep = payload
    },
    setActionType: (
      immerState,
      { payload }: { payload: DepositActionType },
    ) => {
      immerState.actionType = payload
    },
    resetDepositActionState: () => initialState,
  },
})

export const {
  initializeLockFlow,
  initializeWithdrawalFlow,
  initializeMigrationFlow,
  setNewLockDuration,
  setActionStatus,
  setActionType,
  setWithdrawalStep,
  resetDepositActionState,
} = depositActionSlice.actions

export default depositActionSlice.reducer
